import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, EyeOff, MessageSquare, Building2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { fromNow } from "@/lib/time";

export default function AdminQAMonitor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => {
    api.get(`/admin/webinars/${id}`).then((r) => setWebinar(r.data));
    api.get(`/admin/webinars/${id}/questions`).then((r) => setQuestions(r.data));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  const update = async (qid, status) => {
    try {
      await api.patch(`/admin/questions/${qid}`, { status });
      setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, status } : q)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const visible = questions.filter((q) => (filter === "all" ? q.status !== "hidden" : q.status === filter));
  const counts = {
    all: questions.filter((q) => q.status !== "hidden").length,
    submitted: questions.filter((q) => q.status === "submitted").length,
    answered: questions.filter((q) => q.status === "answered").length,
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-4xl">
        <button onClick={() => nav("/admin/webinars")} className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#0A0A0A] mb-5 transition-colors">
          <ArrowLeft size={16} /> Back to webinars
        </button>
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="text-[#0090A8]" size={20} />
          <div className="overline">Live Q&A Monitor</div>
        </div>
        <h1 className="font-display font-black text-3xl tracking-tight mb-1">{webinar?.title || "Webinar"}</h1>
        <p className="text-gray-500 text-sm mb-6">Questions update in real time · {counts.submitted} awaiting answer</p>

        <div className="flex gap-2 mb-5">
          {["all", "submitted", "answered"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`text-sm px-4 py-1.5 rounded-sm border transition-colors capitalize ${filter === f ? "border-[#0090A8] text-[#0090A8] bg-[#0090A8]/10" : "border-[#E4E4E7] text-gray-400 hover:text-[#0A0A0A]"}`}>
              {f} {counts[f] !== undefined ? `(${counts[f]})` : ""}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="border border-dashed border-[#E4E4E7] rounded-sm p-12 text-center text-gray-600">
            <MessageSquare className="mx-auto mb-3 text-gray-700" size={28} />
            No questions {filter !== "all" ? `marked ${filter}` : "yet"}.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-qa-list">
            {visible.map((q) => (
              <div key={q.id} data-testid={`admin-question-${q.id}`} className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="font-semibold text-sm">{q.employee_name}</span>
                      {q.department && <span className="flex items-center gap-1 text-xs text-gray-500"><Building2 size={12} /> {q.department}</span>}
                      <span className="text-xs text-gray-600">{fromNow(q.created_at)}</span>
                      {q.status === "answered" && <span className="text-[0.6rem] font-bold uppercase text-[#12805C] border border-[#12805C]/30 bg-[#12805C]/10 px-2 py-0.5 rounded-sm">Answered</span>}
                    </div>
                    <p className="text-gray-700">{q.question_text}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {q.status !== "answered" && (
                      <button onClick={() => update(q.id, "answered")} data-testid={`mark-answered-${q.id}`} title="Mark answered" className="p-2 text-gray-500 hover:text-[#12805C] transition-colors"><Check size={18} /></button>
                    )}
                    <button onClick={() => update(q.id, "hidden")} data-testid={`hide-${q.id}`} title="Hide" className="p-2 text-gray-500 hover:text-[#D92D20] transition-colors"><EyeOff size={18} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
